"use client"

import { useContext, useEffect, useMemo, useRef, useState } from "react"
import { DataGrid } from "@mui/x-data-grid"
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Button,
  Autocomplete,
  alpha,
  useMediaQuery,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Chip,
} from "@mui/material"
import {
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  LocationOn as LocationOnIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  FilterAltOff as FilterAltOffIcon,
  Delete as DeleteIcon,
  ShoppingCart as ShoppingCartIcon,
  CalendarToday as CalendarTodayIcon,
} from "@mui/icons-material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import dayjs from "dayjs"
import { useTenantNavigation } from "../../../hooks/useTenantNavigation"
import { ErpContext } from "../../../context/ErpContext.js"
import { ApiContextDominios } from "../../../context/apiContextDominios.js"
import ModalViewTablaPreVenta from "./ModalViewTablaPreVenta.js"
import CustomPagination from "../CustomPagination.js"
import "../TablaVentasComponent.css"

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#5b5b5f",
    },
    secondary: {
      main: "rgb(244, 177, 14)",
    },
    background: {
      default: "#f8f9fa",
      paper: "#ffffff",
    },
    text: {
      primary: "#2d3748",
      secondary: "#718096",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    button: {
      textTransform: "none",
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 10,
  },
})

const formatMoney = (value) => {
  const amount = Number.parseFloat(value || 0)
  return amount.toLocaleString("es-AR", { style: "currency", currency: "ARS" })
}

const formatDate = (value) => {
  if (!value) return "-"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "-"
  return dayjs(date).format("DD/MM/YYYY HH:mm")
}

const TablaPreVentasComponent = () => {
  const { isNavbarCollapsed } = useContext(ErpContext)
  const { getPreventas, getUbicacion, ubicaciones, deletePreventa } = useContext(ApiContextDominios)
  const navigate = useTenantNavigation()

  const isLaptopDown = useMediaQuery("(max-width:1200px)")
  const isMobile = useMediaQuery("(max-width:600px)")
  const isFirstRender = useRef(true)
  const today = dayjs()

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMessage, setLoadingMessage] = useState("Cargando preventas...")
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [montoTotalPreventas, setMontoTotalPreventas] = useState(0)
  const [idSearch, setIdSearch] = useState("")
  const [selectedUbicacion, setSelectedUbicacion] = useState(null)
  const [openModal, setOpenModal] = useState(false)
  const [selectedRow, setSelectedRow] = useState(null)
  const [mostrarDeletes, setMostrarDeletes] = useState(false)
  const [startDate, setStartDate] = useState(today.subtract(30, "day"))
  const [endDate, setEndDate] = useState(today)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [preventaToDelete, setPreventaToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const increaseDate = () => setStartDate((prev) => (prev ? prev.add(1, "day") : dayjs()))
  const decreaseDate = () => setStartDate((prev) => (prev ? prev.subtract(1, "day") : dayjs()))

  const fetchData = async (
    date,
    pageNum,
    endDateParam,
    selectedUbicacionParam,
    mostrarDeletesParam,
    idTransaccionSearchParam,
    limitParam = 100,
  ) => {
    try {
      setLoading(true)
      setLoadingMessage("Cargando preventas...")

      const validDate = date || dayjs()
      const formattedDate = validDate.format("YYYY-MM-DD")
      let formattedDate2 = endDateParam
      if (endDateParam && typeof endDateParam.format === "function") {
        formattedDate2 = endDateParam.format("YYYY-MM-DD")
      }

      const response = await getPreventas({
        page: pageNum,
        limit: limitParam,
        idTransaccionSearch: idTransaccionSearchParam || null,
        idUbicacion: selectedUbicacionParam?.id || null,
        mostrarEliminados: mostrarDeletesParam || false,
        fecha: formattedDate,
        formattedDate2,
      })

      setRows(response?.data || [])
      setTotalCount(response?.totalCount || 0)
      setPage(response?.page || pageNum)
      setTotalPages(response?.totalPages || 0)
      const montoTotal = (response?.data || []).reduce((sum, row) => sum + (row.montoTotal || 0), 0)
      setMontoTotalPreventas(montoTotal)
    } catch (error) {
      console.error("Error al cargar preventas:", error)
      setRows([])
      setTotalCount(0)
      setTotalPages(0)
      setMontoTotalPreventas(0)
    } finally {
      setLoading(false)
      setLoadingMessage("")
    }
  }

  useEffect(() => {
    const loadInitialData = async () => {
      const now = dayjs()
      const defaultStartDate = now.subtract(30, "day")
      setStartDate(defaultStartDate)
      setEndDate(now)

      try {
        await getUbicacion()
        await fetchData(defaultStartDate, 1, now, null, false, "", 100)
      } catch (error) {
        console.error("Error al cargar datos iniciales:", error)
      }
    }

    loadInitialData()
  }, [])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    fetchData(startDate, page, endDate, selectedUbicacion, mostrarDeletes, idSearch, 100)
  }, [startDate])

  useEffect(() => {
    if (isFirstRender.current) {
      return
    }
    fetchData(startDate, page, endDate, selectedUbicacion, mostrarDeletes, idSearch, 100)
  }, [page])

  const buttonFiltrado = () => {
    fetchData(startDate, 1, endDate, selectedUbicacion, mostrarDeletes, idSearch, 100)
  }

  const resetFilters = () => {
    setIdSearch("")
    setSelectedUbicacion(null)
    setMostrarDeletes(false)
    const now = dayjs()
    const defaultStartDate = now.subtract(30, "day")
    setStartDate(defaultStartDate)
    setEndDate(now)
    fetchData(defaultStartDate, 1, now, null, false, "", 100)
  }

  const handleToggleEliminados = () => {
    const nuevoEstado = !mostrarDeletes
    setMostrarDeletes(nuevoEstado)
    fetchData(startDate, page, endDate, selectedUbicacion, nuevoEstado, idSearch, 100)
  }

  const handleOpenModal = (row) => {
    setSelectedRow(row)
    setOpenModal(true)
  }

  const handlePasarAVenta = (preventa) => {
    // Navegar a la pantalla de venta con el ID de la preventa
    navigate(`/ventaCrear/${preventa.id}?fromPreventa=true`)
  }

  const handleOpenDeleteModal = (preventa) => {
    setPreventaToDelete(preventa)
    setOpenDeleteModal(true)
  }

  const handleCloseDeleteModal = () => {
    setPreventaToDelete(null)
    setOpenDeleteModal(false)
  }

  const handleConfirmDelete = async () => {
    if (!preventaToDelete) return

    try {
      setIsDeleting(true)
      await deletePreventa(preventaToDelete.id)
      
      // Recargar la tabla
      await fetchData(startDate, page, endDate, selectedUbicacion, mostrarDeletes, idSearch, 100)
      
      handleCloseDeleteModal()
    } catch (error) {
      console.error("Error al eliminar preventa:", error)
      alert("Error al eliminar la preventa. Por favor, intente nuevamente.")
    } finally {
      setIsDeleting(false)
    }
  }

  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "ID",
        width: isLaptopDown ? 70 : 90,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "fechaHoraCreacion",
        headerName: "Fecha",
        minWidth: isLaptopDown ? 130 : 180,
        flex: 1,
        headerAlign: "center",
        align: "center",
        valueGetter: (value, row) => row?.fechaHoraCreacion || row?.createdAt,
        renderCell: (params) => <Typography variant="body2">{formatDate(params.value)}</Typography>,
      },
      {
        field: "cliente",
        headerName: "Cliente",
        minWidth: isLaptopDown ? 180 : 220,
        flex: 1.5,
        headerAlign: "center",
        align: "center",
        valueGetter: (value, row) =>
          `${row?.descripcionEntidad || ""} ${row?.apellido || ""}`.trim() || "Sin cliente",
        renderCell: (params) => (
          <Typography variant="body2" noWrap>
            {params.value}
          </Typography>
        ),
      },
      {
        field: "ubicacion",
        headerName: "Ubicacion",
        minWidth: isLaptopDown ? 120 : 160,
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <LocationOnIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
            <Typography variant="body2">{params.value || "-"}</Typography>
          </Box>
        ),
      },
      {
        field: "usuario",
        headerName: "Vendedor",
        minWidth: isLaptopDown ? 120 : 150,
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontStyle: "italic" }}>
            {params.value || "-"}
          </Typography>
        ),
      },
      {
        field: "montoTotal",
        headerName: "Monto",
        minWidth: isLaptopDown ? 120 : 150,
        flex: 1,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Typography fontWeight={600} color="success.main">
            {formatMoney(params.value)}
          </Typography>
        ),
      },
      {
        field: "accion",
        headerName: "Acciones",
        minWidth: isMobile ? 140 : isLaptopDown ? 180 : 200,
        flex: isMobile ? 0 : 1,
        headerAlign: "center",
        align: "center",
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center", flexWrap: "wrap" }}>
            <Tooltip title="Ver detalle">
              <IconButton
                size="small"
                sx={{
                  bgcolor: alpha("#5b5b5f", 0.1),
                  "&:hover": { bgcolor: alpha("#5b5b5f", 0.2) },
                }}
                onClick={() => handleOpenModal(params.row)}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Pasar a venta">
              <IconButton
                size="small"
                sx={{
                  bgcolor: alpha("rgb(244, 177, 14)", 0.1),
                  color: "rgb(244, 177, 14)",
                  "&:hover": {
                    bgcolor: alpha("rgb(244, 177, 14)", 0.2),
                  },
                }}
                onClick={() => handlePasarAVenta(params.row)}
              >
                <ShoppingCartIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Eliminar preventa">
              <IconButton
                size="small"
                sx={{
                  bgcolor: alpha("#f44336", 0.1),
                  color: "#f44336",
                  "&:hover": {
                    bgcolor: alpha("#f44336", 0.2),
                  },
                }}
                onClick={() => handleOpenDeleteModal(params.row)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [isMobile, isLaptopDown],
  )

  const renderFiltros = () => (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        alignItems: "center",
        justifyContent: "space-between",
        flexDirection: isLaptopDown ? "column" : "row",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.2,
          flex: 1,
          minWidth: isLaptopDown ? "100%" : 250,
          width: isLaptopDown ? "100%" : "auto",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <IconButton onClick={decreaseDate} color="primary" size="small">
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <DatePicker
            value={startDate}
            onChange={(newValue) => setStartDate(newValue)}
            format="DD/MM/YYYY"
            label="Desde"
            slotProps={{ textField: { size: "small", inputProps: { maxLength: 10 }, fullWidth: isLaptopDown } }}
            sx={{ flex: 1 }}
          />
          <DatePicker
            value={endDate}
            onChange={(newValue) => setEndDate(newValue)}
            format="DD/MM/YYYY"
            label="Hasta"
            slotProps={{ textField: { size: "small", inputProps: { maxLength: 10 }, fullWidth: isLaptopDown } }}
            sx={{ flex: 1 }}
          />
          <IconButton onClick={increaseDate} color="primary" size="small">
            <ArrowForwardIcon fontSize="small" />
          </IconButton>
        </LocalizationProvider>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flex: 2,
          minWidth: isLaptopDown ? "100%" : 300,
          width: isLaptopDown ? "100%" : "auto",
          flexDirection: isLaptopDown ? "column" : "row",
        }}
      >
        <TextField
          label="Buscar por ID"
          type="number"
          size="small"
          value={idSearch}
          onChange={(e) => setIdSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: isLaptopDown ? "100%" : "30%" }}
          fullWidth={isLaptopDown}
        />
        <Autocomplete
          size="small"
          disablePortal
          options={(ubicaciones || []).map((it) => ({ id: it.id, label: it.descripcion }))}
          value={selectedUbicacion}
          onChange={(event, newValue) => setSelectedUbicacion(newValue)}
          sx={{ flex: 1, width: isLaptopDown ? "100%" : "auto" }}
          renderInput={(params) => <TextField {...params} label="Ubicacion" variant="outlined" />}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          width: isLaptopDown ? "100%" : "auto",
          justifyContent: isLaptopDown ? "space-between" : "flex-start",
        }}
      >
        <Button
          variant="contained"
          size="small"
          onClick={buttonFiltrado}
          startIcon={<SearchIcon />}
          sx={{
            backgroundColor: "#5b5b5f",
            textTransform: "none",
            padding: "8px 16px",
            color: "white",
            "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
          }}
        >
          Buscar
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={resetFilters}
          startIcon={<FilterAltOffIcon />}
          sx={{
            borderColor: "rgb(244, 177, 14)",
            color: "rgb(244, 177, 14)",
            textTransform: "none",
            padding: "8px 16px",
            "&:hover": {
              borderColor: alpha("rgb(244, 177, 14)", 0.8),
              backgroundColor: alpha("rgb(244, 177, 14)", 0.1),
            },
          }}
        >
          Limpiar
        </Button>
        <Tooltip title={mostrarDeletes ? "Ocultar eliminados" : "Mostrar eliminados"}>
          <IconButton
            onClick={handleToggleEliminados}
            size="small"
            sx={{
              color: mostrarDeletes ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
              "& svg": {
                color: mostrarDeletes ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
              },
              bgcolor: mostrarDeletes ? alpha("rgb(244, 177, 14)", 0.1) : "transparent",
              "&:hover": {
                bgcolor: alpha("rgb(244, 177, 14)", 0.15),
              },
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100%",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <ThemeProvider theme={theme}>
        {loading && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 9999,
            }}
          >
            <Box
              sx={{
                textAlign: "center",
                p: 3,
                borderRadius: 2,
                bgcolor: "background.paper",
                minWidth: 300,
              }}
            >
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {loadingMessage || "Cargando preventas..."}
              </Typography>
            </Box>
          </Box>
        )}

        <Paper
          className="diseñoFiltroTransaccionesVenta"
          elevation={3}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.9),
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            maxHeight: "40vh",
            overflow: "auto",
            flexShrink: 0,
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: alpha(theme.palette.primary.main, 0.4),
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.6),
              },
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: alpha(theme.palette.grey[300], 0.3),
              borderRadius: "4px",
            },
          }}
        >
          {renderFiltros()}
        </Paper>

        <Paper
          sx={{
            flex: 1,
            m: 1,
            borderRadius: 3,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            position: "relative",
            overflow: "hidden",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <DataGrid
            key={isNavbarCollapsed ? "collapsed" : "expanded"}
            className="tablaVentasDataGrid"
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            rowCount={totalPages * 100}
            loading={loading}
            hideFooter
            disableRowSelectionOnClick
            disableColumnMenu
            getRowHeight={() => (isLaptopDown ? 40 : 35)}
            headerHeight={isLaptopDown ? 40 : 45}
            sx={{
              width: "100%",
              border: 0,
              "& .MuiDataGrid-footerContainer": { display: "none !important" },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#5b5b5f",
                color: "white",
                borderBottom: "2px solid #333",
                height: isLaptopDown ? "40px !important" : "45px !important",
                minHeight: isLaptopDown ? "40px !important" : "45px !important",
                maxHeight: isLaptopDown ? "40px !important" : "45px !important",
                padding: isLaptopDown ? "0 8px" : "0 16px",
              },
              "& .MuiDataGrid-columnHeader": {
                height: isLaptopDown ? "40px !important" : "45px !important",
                minHeight: isLaptopDown ? "40px !important" : "45px !important",
                maxHeight: isLaptopDown ? "40px !important" : "45px !important",
                padding: isLaptopDown ? "0 4px" : "0 8px",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "white !important",
                lineHeight: isLaptopDown ? "40px !important" : "45px !important",
                fontSize: isLaptopDown ? "0.75rem" : "0.875rem",
                fontWeight: 600,
              },
              "& .MuiDataGrid-columnHeaderTitle .MuiDataGrid-menuIconButton": {
                color: "white !important",
              },
              "& .MuiDataGrid-menuIconButton": { color: "rgb(244, 177, 14) !important" },
              "& .MuiInputBase-root": { color: "rgb(244, 177, 14) !important" },
              "& .MuiTablePagination-root": { color: "black !important" },
              "& .MuiSelect-icon": { color: "rgb(244, 177, 14) !important" },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #ccc",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: isLaptopDown ? "0 4px" : "0 16px",
              },
              "& .MuiDataGrid-row": {
                "&:nth-of-type(even)": { backgroundColor: "#F7F7F7" },
                "&:hover": { backgroundColor: "#E3AFD !important", transition: "background 0.3s ease" },
              },
              "& .MuiDataGrid-virtualScroller": { lineHeight: "none !important" },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                width: isLaptopDown ? "4px" : "2px",
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "10px",
              },
            }}
          />
        </Paper>

        <Box
          sx={{
            flexShrink: 0,
            p: isLaptopDown ? 0.5 : 1,
            backgroundColor: "white",
            position: "relative",
          }}
        >
          <CustomPagination
            currentPage={page}
            totalPages={totalPages}
            totalCount={totalCount}
            montoTotalTransacciones={montoTotalPreventas}
            vieneDe="tablaVentas"
            onPageChange={(newPage) => setPage(newPage)}
          />
        </Box>

        <ModalViewTablaPreVenta
          openModal={openModal}
          handleCloseModal={() => setOpenModal(false)}
          selectedTransaccion={selectedRow}
        />

        {/* Modal de confirmación de eliminado */}
        <Dialog
          open={openDeleteModal}
          onClose={handleCloseDeleteModal}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            },
          }}
        >
          <DialogTitle
            sx={{
              bgcolor: alpha("#f44336", 0.1),
              color: "#f44336",
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <DeleteIcon />
            Confirmar Eliminación
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <DialogContentText>
              ¿Está seguro que desea eliminar la preventa <strong>#{preventaToDelete?.id}</strong>?
            </DialogContentText>
            <DialogContentText sx={{ mt: 1, color: "text.secondary", fontSize: "0.875rem" }}>
              Esta acción marcará la preventa y todos sus items como eliminados. No afectará el inventario ni las
              cuentas corrientes.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={handleCloseDeleteModal}
              disabled={isDeleting}
              variant="outlined"
              sx={{
                color: "#5b5b5f",
                borderColor: "#5b5b5f",
                "&:hover": {
                  borderColor: "#5b5b5f",
                  bgcolor: alpha("#5b5b5f", 0.1),
                },
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              variant="contained"
              sx={{
                bgcolor: "#f44336",
                "&:hover": {
                  bgcolor: alpha("#f44336", 0.8),
                },
              }}
              startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            >
              {isDeleting ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    </div>
  )
}

export default TablaPreVentasComponent
